import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faGlobe,
  faDesktop,
  faPlus,
  faCamera,
  faSpinner,
  faShieldAlt,
  faTimes,
  faExternalLinkAlt,
  faSave
} from '@fortawesome/free-solid-svg-icons';
import MitreTechniqueSelector from '../mitre/MitreTechniqueSelector';
import targetService from '../../services/targetService';
import { compressImage } from '../../utils/imageUtils';

const ActionDetailEdit = ({
  action,
  projectId,
  isDarkMode,
  availableTargets: initialTargets,
  teamMembers,
  onSave,
  onCancel,
  t,
  saving
}) => {
  const formRef = useRef(null);

  const getLocalDateTime = (date = new Date()) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const [formData, setFormData] = useState({
    title: action.title || '',
    description: action.description || '',
    startTimestamp: action.startTimestamp ? getLocalDateTime(new Date(action.startTimestamp)) : '',
    endTimestamp: action.endTimestamp ? getLocalDateTime(new Date(action.endTimestamp)) : '',
    mitreAttackTechnique: action.mitreAttackTechnique || null,
    result: action.result || 'unknown',
    notes: action.notes || '',
    evidence: action.evidence || [],
    operator: action.operator || ''
  });

  const [selectedTargets, setSelectedTargets] = useState(() => {
    return (action.targets || []).map(t => typeof t === 'object' ? t._id : t);
  });
  const [selectedSources, setSelectedSources] = useState(() => {
    return (action.sources || []).map(s => typeof s === 'object' ? s._id : s);
  });

  const [availableTargets, setAvailableTargets] = useState(initialTargets || []);
  const [showTechniqueSelector, setShowTechniqueSelector] = useState(false);
  const [showAddTargetModal, setShowAddTargetModal] = useState(false);
  const [newTarget, setNewTarget] = useState({ type: 'hostname', value: '', description: '' });
  const [addingTarget, setAddingTarget] = useState(false);
  const [error, setError] = useState('');

  // Sync available targets from parent
  useEffect(() => {
    if (initialTargets) setAvailableTargets(initialTargets);
  }, [initialTargets]);

  const inputCls = `w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 text-sm ${
    isDarkMode
      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
  }`;

  const labelCls = `block text-sm font-bold mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleTechniqueSelect = (technique) => {
    setFormData(prev => ({
      ...prev,
      mitreAttackTechnique: {
        id: technique.id,
        name: technique.name,
        tactic: technique.tactic,
        url: technique.url
      }
    }));
    setShowTechniqueSelector(false);
  };

  const handleTargetToggle = (targetId) => {
    setSelectedTargets(prev =>
      prev.includes(targetId) ? prev.filter(id => id !== targetId) : [...prev, targetId]
    );
  };

  const handleSourceToggle = (sourceId) => {
    setSelectedSources(prev =>
      prev.includes(sourceId) ? prev.filter(id => id !== sourceId) : [...prev, sourceId]
    );
  };

  const handleEvidenceAdd = () => {
    const url = prompt(t('actions.addUrl') + ':');
    if (url) {
      setFormData(prev => ({ ...prev, evidence: [...prev.evidence, url] }));
    }
  };

  const handleEvidenceFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    for (const file of files) {
      if (file.type.startsWith('image/')) {
        try {
          const compressedBase64 = await compressImage(file, 1200, 1200, 0.7);
          setFormData(prev => ({
            ...prev,
            evidence: [...prev.evidence, compressedBase64]
          }));
        } catch (err) {
          console.error('Error compressing image:', err);
        }
      }
    }
    e.target.value = null;
  };

  const handleEvidenceRemove = (index) => {
    setFormData(prev => ({
      ...prev,
      evidence: prev.evidence.filter((_, i) => i !== index)
    }));
  };

  const handleAddTarget = async () => {
    if (!newTarget.value.trim()) return;
    setAddingTarget(true);
    try {
      const createdTarget = await targetService.createTarget({
        project: projectId,
        type: newTarget.type,
        value: newTarget.value.trim(),
        description: newTarget.description.trim()
      });
      setAvailableTargets(prev => [...prev, createdTarget]);
      setNewTarget({ type: 'hostname', value: '', description: '' });
      setShowAddTargetModal(false);
    } catch (err) {
      console.error('Error creating target:', err);
      alert(err.response?.data?.message || 'Failed to create target');
    } finally {
      setAddingTarget(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    if (!formData.title.trim()) {
      setError(t('actions.actionTitle') + ' is required');
      return;
    }
    onSave({
      ...formData,
      targets: selectedTargets,
      sources: selectedSources,
      project: action.project?._id || action.project || projectId
    });
  };

  return (
    <>
      <form ref={formRef} onSubmit={handleSubmit} className="flex flex-col h-full">
        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 px-4 py-3 rounded text-sm">
              {error}
            </div>
          )}

          {/* Title */}
          <div>
            <label className={labelCls} htmlFor="edit-title">{t('actions.actionTitle')} *</label>
            <input
              type="text"
              id="edit-title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className={inputCls}
              required
              disabled={saving}
            />
          </div>

          {/* Description */}
          <div>
            <label className={labelCls} htmlFor="edit-description">{t('common.description')}</label>
            <textarea
              id="edit-description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="3"
              className={inputCls}
              disabled={saving}
            />
          </div>

          {/* Timestamps */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls} htmlFor="edit-startTimestamp">{t('actions.startTime')}</label>
              <input
                type="datetime-local"
                id="edit-startTimestamp"
                name="startTimestamp"
                value={formData.startTimestamp}
                onChange={handleChange}
                className={inputCls}
                disabled={saving}
              />
            </div>
            <div>
              <label className={labelCls} htmlFor="edit-endTimestamp">{t('actions.endTime')}</label>
              <input
                type="datetime-local"
                id="edit-endTimestamp"
                name="endTimestamp"
                value={formData.endTimestamp}
                onChange={handleChange}
                className={inputCls}
                disabled={saving}
              />
            </div>
          </div>

          {/* MITRE Technique + Result */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>MITRE ATT&CK Technique</label>
              <button
                type="button"
                onClick={() => setShowTechniqueSelector(true)}
                className={`w-full px-3 py-2 border rounded-md text-left flex items-center justify-between text-sm ${
                  formData.mitreAttackTechnique
                    ? isDarkMode ? 'border-blue-500 bg-blue-900/20 text-white' : 'border-blue-500 bg-blue-50 text-gray-900'
                    : isDarkMode ? 'border-gray-600 bg-gray-700 text-gray-400' : 'border-gray-300 text-gray-500'
                }`}
                disabled={saving}
              >
                {formData.mitreAttackTechnique ? (
                  <div className="flex items-center flex-1 min-w-0">
                    <span className="font-mono text-xs px-2 py-0.5 bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300 rounded mr-2">
                      {formData.mitreAttackTechnique.id}
                    </span>
                    <span className="truncate">{formData.mitreAttackTechnique.name}</span>
                  </div>
                ) : (
                  <span>{t('actions.clickToSelectTechnique')}</span>
                )}
                <FontAwesomeIcon icon={faShieldAlt} className="ml-2 opacity-50" />
              </button>
              {formData.mitreAttackTechnique && (
                <div className="mt-1 flex items-center justify-between">
                  <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {formData.mitreAttackTechnique.tactic}
                  </span>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, mitreAttackTechnique: null }))}
                    className="text-xs text-red-500 hover:text-red-700"
                  >
                    <FontAwesomeIcon icon={faTimes} className="mr-1" />
                    {t('common.clear')}
                  </button>
                </div>
              )}
            </div>
            <div>
              <label className={labelCls} htmlFor="edit-result">{t('actions.result')} *</label>
              <select
                id="edit-result"
                name="result"
                value={formData.result}
                onChange={handleChange}
                className={inputCls}
                required
                disabled={saving}
              >
                <option value="unknown">{t('actions.unknown')}</option>
                <option value="successful">{t('actions.successful')}</option>
                <option value="detected">{t('actions.detected')}</option>
                <option value="blocked">{t('actions.blocked')}</option>
                <option value="failed">{t('actions.failed')}</option>
                <option value="partial">{t('actions.partial')}</option>
              </select>
            </div>
          </div>

          {/* Operator */}
          <div>
            <label className={labelCls} htmlFor="edit-operator">{t('actions.operator')}</label>
            {teamMembers && teamMembers.length > 0 ? (
              <select
                id="edit-operator"
                name="operator"
                value={formData.operator}
                onChange={handleChange}
                className={inputCls}
                disabled={saving}
              >
                <option value="">{t('actions.selectOperator')}</option>
                {teamMembers.map((member, index) => (
                  <option key={index} value={member.name}>
                    {member.name} - {member.role}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                id="edit-operator"
                name="operator"
                value={formData.operator}
                onChange={handleChange}
                className={inputCls}
                disabled={saving}
                placeholder={t('actions.enterOperatorName')}
              />
            )}
          </div>

          {/* Source + Target Selection */}
          <div className="grid grid-cols-2 gap-4">
            {/* Sources */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className={labelCls}>{t('actions.sourceAttackOrigin')}</label>
                <button
                  type="button"
                  onClick={() => setShowAddTargetModal(true)}
                  className="text-xs text-orange-500 hover:text-orange-700"
                >
                  <FontAwesomeIcon icon={faPlus} className="mr-1" />
                  {t('actions.addTarget')}
                </button>
              </div>
              <div className={`rounded-lg p-3 max-h-40 overflow-y-auto ${isDarkMode ? 'bg-gray-700/50' : 'bg-orange-50'}`}>
                {availableTargets.length === 0 ? (
                  <p className={`text-xs text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {t('actions.noTargetsDefined')}
                  </p>
                ) : (
                  <div className="space-y-1">
                    {availableTargets.map((target) => (
                      <label
                        key={target._id}
                        className={`flex items-center p-1.5 rounded cursor-pointer text-sm ${
                          isDarkMode ? 'hover:bg-gray-600' : 'hover:bg-orange-100'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedSources.includes(target._id)}
                          onChange={() => handleSourceToggle(target._id)}
                          className="mr-2 h-3.5 w-3.5 text-orange-600 rounded"
                          disabled={saving}
                        />
                        <FontAwesomeIcon
                          icon={target.type === 'ip' ? faGlobe : faDesktop}
                          className={`mr-1.5 text-xs ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}
                        />
                        <span className={isDarkMode ? 'text-gray-200' : 'text-gray-800'}>{target.value}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Targets */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className={labelCls}>{t('actions.affectedTargets')}</label>
                <button
                  type="button"
                  onClick={() => setShowAddTargetModal(true)}
                  className="text-xs text-blue-500 hover:text-blue-700"
                >
                  <FontAwesomeIcon icon={faPlus} className="mr-1" />
                  {t('actions.addTarget')}
                </button>
              </div>
              <div className={`rounded-lg p-3 max-h-40 overflow-y-auto ${isDarkMode ? 'bg-gray-700/50' : 'bg-blue-50'}`}>
                {availableTargets.length === 0 ? (
                  <p className={`text-xs text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {t('actions.noTargetsDefined')}
                  </p>
                ) : (
                  <div className="space-y-1">
                    {availableTargets.map((target) => (
                      <label
                        key={target._id}
                        className={`flex items-center p-1.5 rounded cursor-pointer text-sm ${
                          isDarkMode ? 'hover:bg-gray-600' : 'hover:bg-blue-100'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedTargets.includes(target._id)}
                          onChange={() => handleTargetToggle(target._id)}
                          className="mr-2 h-3.5 w-3.5 text-blue-600 rounded"
                          disabled={saving}
                        />
                        <FontAwesomeIcon
                          icon={target.type === 'ip' ? faGlobe : faDesktop}
                          className={`mr-1.5 text-xs ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}
                        />
                        <span className={isDarkMode ? 'text-gray-200' : 'text-gray-800'}>{target.value}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className={labelCls} htmlFor="edit-notes">{t('actions.additionalNotes')}</label>
            <textarea
              id="edit-notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows="3"
              className={inputCls}
              disabled={saving}
              placeholder={t('actions.notesPlaceholder')}
            />
          </div>

          {/* Evidence */}
          <div>
            <label className={labelCls}>{t('actions.evidence')}</label>
            <div className="space-y-2">
              {formData.evidence.map((item, index) => (
                <div key={index} className="flex items-center space-x-2">
                  {(typeof item === 'string' && item.startsWith('data:image')) ? (
                    <div className={`flex items-center flex-1 p-2 rounded-md ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                      <img src={item} alt="Evidence" className="h-16 w-16 object-cover rounded mr-3" />
                      <span className={`text-sm flex-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        Screenshot {index + 1}
                      </span>
                    </div>
                  ) : (typeof item === 'object' && item.type === 'screenshot' && item.data) ? (
                    <div className={`flex items-center flex-1 p-2 rounded-md ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                      <img src={item.data} alt={item.caption || 'Evidence'} className="h-16 w-16 object-cover rounded mr-3" />
                      <span className={`text-sm flex-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        {item.caption || `Screenshot ${index + 1}`}
                      </span>
                    </div>
                  ) : (
                    <div className={`flex items-center flex-1 p-2 rounded-md text-sm ${isDarkMode ? 'bg-gray-700 text-blue-400' : 'bg-gray-50 text-blue-600'}`}>
                      <FontAwesomeIcon icon={faExternalLinkAlt} className="mr-2" />
                      <span className="truncate">{typeof item === 'string' ? item : (item.url || JSON.stringify(item))}</span>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => handleEvidenceRemove(index)}
                    className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 flex-shrink-0"
                    disabled={saving}
                  >
                    {t('common.remove')}
                  </button>
                </div>
              ))}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleEvidenceAdd}
                  className={`px-3 py-1.5 text-sm rounded-md ${isDarkMode ? 'bg-gray-600 text-gray-200 hover:bg-gray-500' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                  disabled={saving}
                >
                  <FontAwesomeIcon icon={faPlus} className="mr-1" />
                  {t('actions.addUrl')}
                </button>
                <label className={`px-3 py-1.5 text-sm rounded-md cursor-pointer ${isDarkMode ? 'bg-blue-700 text-white hover:bg-blue-600' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>
                  <FontAwesomeIcon icon={faCamera} className="mr-1" />
                  {t('actions.addScreenshot')}
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleEvidenceFileUpload}
                    className="hidden"
                    disabled={saving}
                  />
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Sticky footer */}
        <div className={`sticky bottom-0 px-6 py-4 border-t flex justify-end gap-3 ${
          isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <button
            type="button"
            onClick={onCancel}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              isDarkMode ? 'bg-gray-600 text-gray-200 hover:bg-gray-500' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
            disabled={saving}
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed flex items-center"
            disabled={saving}
          >
            {saving ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                {t('common.saving')}
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} className="mr-2" />
                {t('actions.saveChanges') || t('common.save')}
              </>
            )}
          </button>
        </div>
      </form>

      {/* MITRE Technique Selector Modal - z-[60] to sit above parent modal */}
      {showTechniqueSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className={`rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-hidden ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className={`flex items-center justify-between p-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h2 className={`text-xl font-bold flex items-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                <FontAwesomeIcon icon={faShieldAlt} className="mr-2 text-red-600" />
                {t('actions.selectMitreTechnique')}
              </h2>
              <button
                type="button"
                onClick={() => setShowTechniqueSelector(false)}
                className={`p-2 rounded-full transition-colors ${isDarkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'}`}
              >
                <FontAwesomeIcon icon={faTimes} className="text-xl" />
              </button>
            </div>
            <MitreTechniqueSelector
              onSelect={handleTechniqueSelect}
              selectedTechnique={formData.mitreAttackTechnique?.id}
              onClose={() => setShowTechniqueSelector(false)}
              darkMode={isDarkMode}
            />
          </div>
        </div>
      )}

      {/* Add Target Modal - z-[60] */}
      {showAddTargetModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className={`rounded-lg shadow-xl w-full max-w-md ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className={`flex items-center justify-between p-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h2 className={`text-lg font-bold flex items-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                <FontAwesomeIcon icon={faPlus} className="mr-2 text-blue-600" />
                {t('targets.addNewTarget')}
              </h2>
              <button
                type="button"
                onClick={() => { setShowAddTargetModal(false); setNewTarget({ type: 'hostname', value: '', description: '' }); }}
                className={`p-2 rounded-full transition-colors ${isDarkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'}`}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className={labelCls}>{t('targets.targetType')}</label>
                <select
                  value={newTarget.type}
                  onChange={(e) => setNewTarget(prev => ({ ...prev, type: e.target.value }))}
                  className={inputCls}
                >
                  <option value="hostname">{t('targets.hostname')}</option>
                  <option value="ip">{t('targets.ipAddress')}</option>
                  <option value="url">{t('targets.url')}</option>
                  <option value="range">{t('targets.ipRange')}</option>
                  <option value="subnet">{t('targets.subnet')}</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>{t('common.value')} *</label>
                <input
                  type="text"
                  value={newTarget.value}
                  onChange={(e) => setNewTarget(prev => ({ ...prev, value: e.target.value }))}
                  placeholder={
                    newTarget.type === 'ip' ? '192.168.1.100' :
                    newTarget.type === 'hostname' ? 'server.local' :
                    newTarget.type === 'url' ? 'https://example.com' :
                    newTarget.type === 'range' ? '192.168.1.1-192.168.1.254' :
                    '192.168.1.0/24'
                  }
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>{t('common.descriptionOptional')}</label>
                <input
                  type="text"
                  value={newTarget.description}
                  onChange={(e) => setNewTarget(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="e.g., Web server, Domain controller"
                  className={inputCls}
                />
              </div>
            </div>
            <div className={`p-4 border-t flex justify-end gap-3 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <button
                type="button"
                onClick={() => { setShowAddTargetModal(false); setNewTarget({ type: 'hostname', value: '', description: '' }); }}
                className={`px-4 py-2 rounded-md text-sm ${isDarkMode ? 'bg-gray-600 text-gray-200 hover:bg-gray-500' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleAddTarget}
                disabled={addingTarget || !newTarget.value.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center"
              >
                {addingTarget ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                    {t('common.adding')}
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faPlus} className="mr-2" />
                    {t('actions.addTarget')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ActionDetailEdit;
